import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useBrand, BrandLogo } from "@/lib/brand";
import { Loader2, Calendar, Lock, Activity } from "lucide-react";

/**
 * Public walkthrough share page — `/share/walkthrough/:id?k=<token>`.
 * No auth required. We use raw axios so the api interceptor's Bearer header
 * doesn't conflict with the share-token flow.
 */
export default function WalkthroughShare() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { resolveBy } = useBrand();
  const k = sp.get("k") || "";
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const viewLoggedRef = useRef(false);

  const sessionIdRef = useRef("");
  const lastBeatRef = useRef(0);

  useEffect(() => {
    if (!id || !k) { setError("This share link is missing its access token."); return; }
    let alive = true;
    resolveBy("walkthrough", id);
    (async () => {
      try {
        const r = await axios.get(`${API}/walkthroughs/${id}`, { params: { k } });
        if (!alive) return;
        setMeta(r.data);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.detail || "Couldn't load this walkthrough.");
      }
    })();
    return () => { alive = false; };
  }, [id, k]);

  // Generate a per-tab session ID once
  if (!sessionIdRef.current) {
    sessionIdRef.current = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  const sendHeartbeat = async (videoEl, completed = false) => {
    if (!id || !k) return;
    try {
      await axios.post(
        `${API}/walkthroughs/${id}/heartbeat`,
        {
          session_id: sessionIdRef.current,
          progress_s: videoEl?.currentTime || 0,
          completed,
        },
        { params: { k } },
      );
    } catch { /* swallow */ }
  };

  const onTimeUpdate = (e) => {
    const t = e.target.currentTime;
    if (t - lastBeatRef.current > 5 || (t < lastBeatRef.current)) {
      lastBeatRef.current = t;
      sendHeartbeat(e.target, false);
    }
  };

  const onPlay = async (e) => {
    if (!viewLoggedRef.current) {
      viewLoggedRef.current = true;
      sendHeartbeat(e.target, false);
    }
  };

  const onEnded = (e) => sendHeartbeat(e.target, true);

  const videoSrc = id && k ? `${API}/walkthroughs/${id}/video?k=${encodeURIComponent(k)}` : "";

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-zinc-900 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandLogo size="md" />
        </Link>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// SHARED WALKTHROUGH</div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10" data-testid="walkthrough-share-page">
        {!meta && !error && (
          <div className="flex flex-col items-center justify-center py-32 gap-3 text-zinc-500 font-mono text-[11px]">
            <Loader2 className="w-6 h-6 animate-spin text-[#FFB000]" />
            LOADING WALKTHROUGH…
          </div>
        )}

        {error && (
          <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-8 text-center max-w-md mx-auto">
            <Lock className="w-7 h-7 text-[#FF3B30] mx-auto mb-3" />
            <div className="font-heading font-bold text-xl mb-2">Can&apos;t access this walkthrough</div>
            <div className="font-mono text-[11px] text-zinc-400">{error}</div>
            <Link to="/" style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }} className="inline-block mt-6 font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2.5 hover:opacity-90 transition-opacity" data-testid="walkthrough-share-home">
              GO TO DARA TERMINAL
            </Link>
          </div>
        )}

        {meta && (
          <>
            <div className="mb-5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// WALKTHROUGH</div>
              <h1 className="font-heading font-black text-4xl tracking-tight mt-1">{meta.title}</h1>
              <div className="flex items-center gap-4 mt-2 font-mono text-[11px] text-zinc-500">
                <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(meta.created_at).toLocaleString()}</span>
                <span>{Math.round(meta.duration_s)}s</span>
                {meta.has_audio && <span className="text-[#00E676]">narrated</span>}
                {meta.view_count > 0 && (
                  <span className="flex items-center gap-1.5 text-[#FFB000]"><Activity className="w-3.5 h-3.5" /> {meta.view_count} view{meta.view_count === 1 ? "" : "s"}</span>
                )}
              </div>
              <div className="font-mono text-[11px] text-zinc-400 mt-1">
                Recorded by <span className="text-white">{meta.user_name}</span>
                {meta.company && <> · {meta.company}</>}
              </div>
            </div>

            <video
              key={videoSrc}
              controls autoPlay
              onPlay={onPlay}
              onTimeUpdate={onTimeUpdate}
              onEnded={onEnded}
              src={videoSrc}
              className="w-full bg-zinc-950 border border-zinc-900"
              data-testid="walkthrough-share-video"
            />

            <div className="mt-10 border border-zinc-900 p-6 bg-gradient-to-br from-zinc-950 to-black">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">// NEXT STEP</div>
              <h2 className="font-heading font-black text-2xl tracking-tight mt-1 mb-2">See DARA running on your own data.</h2>
              <p className="text-sm text-zinc-400 max-w-2xl mb-4">
                You just watched a real walkthrough captured inside DARA. Book a 30-minute working session with our team
                and we&apos;ll wire your warehouse, run the Insight Engine on your real data, and show the revenue map for your business.
              </p>
              <Link to="/?intent=enterprise#contact" style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }} className="inline-block font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 transition-opacity" data-testid="walkthrough-share-cta">
                BOOK A WORKING SESSION →
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
